import styled, { useTheme } from 'styled-components';
import Button from '../../../../components/Button';
import ColorizedSpan from '../../../../components/Colorized';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import WidthContainer from '../../../../components/WidthContainer';
import {
  PHONE_MEDIA_QUERY,
  useMediaQuery,
} from '../../../../common/responsive';
import { hapticsImpactLight } from '../../../../common/haptic';
import { useNavigate } from 'react-router-dom';
import { useFlag } from '@unleash/proxy-client-react';
import { useSelector } from 'react-redux';
import { selectThemeMode } from '../../../../redux/slices/themeSlice';
import {
  sendEvent,
  sendMetaEvent,
  EVENT_SPEND_GALLERY_OPENED_FROM_LANDING_PAGE,
  EVENT_SPEND_GALLERY_CARD_CLICKED,
} from '../../../../common/analytics';
import { useGetGiftCardCatalogueQuery } from '../../../../redux/api/public/globetopper';
import { selectGiftCardsForCountry } from '../../../../redux/slices/giftCardCatalogue';
import SpendGalleryGiftCard from '../../../Spend/SpendGallery/SpendGalleryGiftCard';
import useCustomLogin from '../../../../hooks/useCustomLogin';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-direction: row;
  width: 100%;
  height: fit-content;
  min-height: 500px;
  position: relative;
  justify-content: space-between;
  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    padding-top: 50px;
    text-align: center;
  }
`;

const Title = styled.h1`
  font-size: 64px;
  font-weight: 500;
  line-height: 77px;
  letter-spacing: -2%;
  color: ${({ theme }) => theme.text.title};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 2.5rem;
    line-height: 2.8rem;
  }
`;

const SubTitle = styled.h2`
  font-size: 20px;
  font-weight: 400;
  line-height: 29px;
  letter-spacing: -2%;
  color: ${({ theme }) => theme.text.title};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 1rem;
    line-height: 1.2rem;
  }
`;

const Column = styled.div`
  width: 100%;
  display: flex;
  gap: 30px;
  flex-direction: column;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 15px;
  }
`;

const MobileImage = styled.img`
  width: 436px;
  object-fit: cover;
  user-select: none;
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-drag: none;
  cursor: pointer;
  @media ${PHONE_MEDIA_QUERY} {
    max-width: 300px;
    margin-top: 25px;
  }
`;

const ContainerBackground = styled.div`
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.background.primary} 0%,
    ${({ theme }) => theme.primary.background} 100%
  );
  position: absolute;
  width: 100%;
  left: 0px;
`;

const BackedByAlliance = styled.div`
  position: absolute;
  bottom: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding-bottom: 20px;
  @media ${PHONE_MEDIA_QUERY} {
    padding-bottom: 10px;
  }
`;

const GiftCardsSectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: ${({ theme }) => theme.text.title};
  margin: 0 0 16px 0;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 18px;
    margin-bottom: 12px;
    text-align: center;
  }
`;

const HeroGiftCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  max-width: 428px;
  @media ${PHONE_MEDIA_QUERY} {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    max-width: 100%;
  }
`;

const IMAGER_MAPPER = {
  default: '/landing-v2/hero-mobile-test.webp',
  spend: '/landing-v2/airbnb-playstation.webp',
  //default: '/landing-v2/gift-mobile2.webp',
  stocks: '/landing-v2/hero-mobile-tesla.webp',
  netflix: '/landing-v2/hero-mobile-netflix.webp',
  gold: '/landing-v2/hero-mobile-gold.webp',
  mcdonalds: '/landing-v2/hero-mobile-mcdonalds.webp',
  microsoft: '/landing-v2/hero-mobile-microsoft.webp',
  cocacola: '/landing-v2/hero-mobile-cocacola.webp',
  apple: '/landing-v2/hero-mobile-apple.webp',
  openai: '/landing-v2/hero-mobile-openai.webp',
  checkfreegift: '/landing-v2/hero-mobile-bitcoin.webp',
  yield: '/landing-v2/yield.webp',
  payments: '/paymentLink.webp',
};

const HEADLINE_MAPPER = {
  default: (
    <>
      Gift crypto <br />
      without explaining it.
    </>
  ),
  spend: (
    <>
      {' '}
      Turn crypto into
      <br />
      everyday spending
      <br />
      Get 2% back in Solana
    </>
  ),
  stocks: 'Stocks',
  netflix: 'Stocks',
  gold: 'Stocks',
  mcdonalds: 'Stocks',
  microsoft: 'Stocks',
  cocacola: 'Stocks',
  apple: 'Stocks',
  openai: 'PreStock',
  checkfreegift: 'Crypto',
  yield: 'Gift cards that earn 5-7% while you wait.',
  payments: (
    <>
      Get paid <br />
      without banks
    </>
  ),
};

const StocksSubline = (theme) => (
  <SubTitle style={{ color: theme.text.regular }}>
    Instant, personalized stock gifts. No brokerage accounts, <br /> powered by
    blockchain technology.
  </SubTitle>
);

const PreStockSubline = (theme) => (
  <SubTitle style={{ color: theme.text.regular }}>
    Instant, personalized pre-stock gifts. No brokerage accounts, <br /> powered
    by blockchain technology.
  </SubTitle>
);

const DefaultSubline = (theme) => (
  <SubTitle style={{ color: theme.text.regular }}>
    Send crypto and stocks that can be used <br />
    like a gift card — or kept as a portfolio.
  </SubTitle>
);

const SpendSubline = (theme) => (
  <SubTitle style={{ color: theme.text.regular }}>
    Get instant gift cards for top brands and earn <br />
    cashback on every purchase.
  </SubTitle>
);

const YieldSubline = (theme) => (
  <SubTitle style={{ color: theme.text.regular }}>
    Spend it anytime — or let its value grow while you wait.
  </SubTitle>
);

const PaymentsSubline = (theme) => (
  <SubTitle style={{ color: theme.text.regular }}>
    Send a payment link. Your customer can pay with cards or bank transfer.{' '}
    <br />
    You receive crypto instantly — no bank account, no delays, no
    intermediaries.
  </SubTitle>
);

const SUBLINE_MAPPER = {
  default: DefaultSubline,
  spend: SpendSubline,
  stocks: StocksSubline,
  netflix: StocksSubline,
  gold: StocksSubline,
  mcdonalds: StocksSubline,
  microsoft: StocksSubline,
  cocacola: StocksSubline,
  apple: StocksSubline,
  openai: PreStockSubline,
  checkfreegift: DefaultSubline,
  yield: YieldSubline,
  payments: PaymentsSubline,
};

const Hero = () => {
  const backgroundRef = useRef(null);
  const [siblingHeight, setSiblingHeight] = useState(undefined);
  const [searchParams] = useSearchParams();
  const lpd = searchParams.get('lpd');
  const navigate = useNavigate();
  const { login } = useCustomLogin();
  const showFreeGift = useFlag('showFreeGift');
  const showPrestockBonus = useFlag('showPrestockBonus');
  const selectedTheme = useSelector(selectThemeMode);
  const [animatedValue, setAnimatedValue] = useState(100.0);
  const [textColor, setTextColor] = useState('black');
  const animationRef = useRef(null);
  const colorAnimationRef = useRef(null);
  const valueIncrementedRef = useRef(false);
  const incrementIntervalRef = useRef(null);

  useEffect(() => {
    const updateHeight = () => {
      if (backgroundRef.current) {
        setSiblingHeight(backgroundRef.current.offsetHeight);
      }
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);

    if (backgroundRef.current) {
      observer.observe(backgroundRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const animateDecimalIncrement = useCallback(
    (targetValue, shouldContinue = false) => {
      // Reset the increment flag
      valueIncrementedRef.current = false;

      // Animate color: black -> green -> black
      let colorStartTime = null;
      const colorDuration = 1500; // 1.5 seconds for color animation

      const animateColor = (timestamp) => {
        if (!colorStartTime) colorStartTime = timestamp;
        const progress = Math.min(
          (timestamp - colorStartTime) / colorDuration,
          1,
        );

        if (progress < 0.5) {
          // Fade to green (first half)
          const greenProgress = progress * 2;
          const r = Math.round(0 * (1 - greenProgress) + 34 * greenProgress);
          const g = Math.round(0 * (1 - greenProgress) + 197 * greenProgress);
          const b = Math.round(0 * (1 - greenProgress) + 94 * greenProgress);
          setTextColor(`rgb(${r}, ${g}, ${b})`);

          // Increment value when we're halfway to green
          if (greenProgress > 0.5 && !valueIncrementedRef.current) {
            setAnimatedValue(targetValue);
            valueIncrementedRef.current = true;
          }
        } else {
          // Fade back to black (second half)
          const blackProgress = (progress - 0.5) * 2;
          const r = Math.round(34 * (1 - blackProgress) + 0 * blackProgress);
          const g = Math.round(197 * (1 - blackProgress) + 0 * blackProgress);
          const b = Math.round(94 * (1 - blackProgress) + 0 * blackProgress);
          setTextColor(`rgb(${r}, ${g}, ${b})`);
        }

        if (progress < 1) {
          colorAnimationRef.current = requestAnimationFrame(animateColor);
        } else {
          setTextColor('black');

          // If we should continue, schedule the next increment after 1 second
          if (shouldContinue) {
            incrementIntervalRef.current = setTimeout(() => {
              const nextValue = parseFloat((targetValue + 0.01).toFixed(2));
              animateDecimalIncrement(nextValue, true);
            }, 1000);
          }
        }
      };

      colorAnimationRef.current = requestAnimationFrame(animateColor);
    },
    [],
  );

  // Animate value increasing from 100.00
  useEffect(() => {
    if (lpd !== 'yield') {
      // Reset values when not in yield mode
      setAnimatedValue(100.0);
      setTextColor('black');
      valueIncrementedRef.current = false;
      // Clear any ongoing intervals
      if (incrementIntervalRef.current) {
        clearTimeout(incrementIntervalRef.current);
        incrementIntervalRef.current = null;
      }
      return;
    }

    // Reset state for new animation
    setAnimatedValue(0);
    setTextColor('black');
    valueIncrementedRef.current = false;

    let startTime = null;
    const duration = 2000; // 2 seconds to reach 100.00
    const startValue = 0;
    const endValue = 100.0;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      // Easing function for smooth animation
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + (endValue - startValue) * easeOutCubic;

      setAnimatedValue(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // After reaching 100.00, immediately start infinite decimal increments
        animateDecimalIncrement(100.01, true);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (colorAnimationRef.current) {
        cancelAnimationFrame(colorAnimationRef.current);
      }
      if (incrementIntervalRef.current) {
        clearTimeout(incrementIntervalRef.current);
        incrementIntervalRef.current = null;
      }
    };
  }, [lpd, animateDecimalIncrement]);

  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const theme = useTheme();

  const queryCountryCode = '';
  const { data: _catalogueData, isLoading: isLoadingCatalogue } =
    useGetGiftCardCatalogueQuery(queryCountryCode, {
      skip: lpd !== 'spend',
    });
  const allGiftCards = useSelector((state) =>
    selectGiftCardsForCountry(state, lpd === 'spend' ? queryCountryCode : ''),
  );
  const heroGiftCards = useMemo(() => {
    if (lpd !== 'spend' || !allGiftCards?.length) return [];
    const categoryId = 0;
    const filtered = allGiftCards.filter((card) =>
      (card.detailedInfo?.categories || []).some(
        (cat) => cat.id === categoryId,
      ),
    );
    return filtered.length > 0
      ? filtered.slice(0, 6)
      : allGiftCards.slice(0, 6);
  }, [lpd, allGiftCards]);

  const buildCreateUrl = () => {
    if (lpd === 'spend') {
      return '/app/spend?lpd=spend';
    }
    const params = new URLSearchParams();
    if (lpd) {
      params.set('lpd', lpd);
    }
    if (lpd && lpd !== 'openai' && lpd !== 'yield' && showFreeGift) {
      params.set('trial', '1');
    }
    if (lpd === 'yield') {
      params.set(
        'tokenAddress',
        'AvZZF1YaZDziPY2RCK4oJrRVrbN3mTD9NL24hPeaZeUj',
      );
      params.set('network', 'solana');
    }
    const query = params.toString();
    return query ? `/app/gifts/create?${query}` : '/app/gifts/create';
  };

  const getCtaLabel = () => {
    if (lpd === 'payments') return 'Create a payment link';
    if (lpd === 'spend') return 'Start Shopping';
    if (
      lpd &&
      lpd !== 'openai' &&
      lpd !== 'spend' &&
      lpd !== 'yield' &&
      showFreeGift
    ) {
      return 'Create Your First Gift Free';
    }
    return 'Create Your First Gift';
  };

  const handleCtaClick = () => {
    if (lpd === 'payments') {
      login();
      return;
    }
    navigate(buildCreateUrl());
    if (lpd === 'spend') {
      sendEvent(EVENT_SPEND_GALLERY_OPENED_FROM_LANDING_PAGE);
    }
  };

  return (
    <>
      <ContainerBackground ref={backgroundRef}>
        <WidthContainer>
          <Container
            style={
              lpd === 'spend' && isMobile
                ? { minHeight: 0, paddingTop: 24, paddingBottom: 24 }
                : undefined
            }
          >
            <Column
              style={
                lpd === 'spend' && !isMobile
                  ? { width: '65%', maxWidth: '65%' }
                  : undefined
              }
            >
              <Title
                style={
                  lpd === 'spend' && isMobile
                    ? {
                        marginTop: '15px',
                        fontSize: '1.8rem',
                        lineHeight: '2rem',
                      }
                    : lpd === 'spend' && !isMobile
                      ? {
                          marginTop: '30px',
                          fontSize: '60px',
                          lineHeight: '72px',
                        }
                      : isMobile
                        ? { fontSize: '30px', marginTop: '-20px' }
                        : {}
                }
              >
                <>{HEADLINE_MAPPER[lpd || 'default']}</>
              </Title>
              {/* <Title>
                {lpd ? (
                  <>
                    The Easiest Way <br />
                    to <ColorizedSpan>Gift</ColorizedSpan>{' '}
                    {HEADLINE_MAPPER[lpd]}
                  </>
                ) : (
                  <>
                    The Easiest Way <br />
                    to <ColorizedSpan>Gift</ColorizedSpan> Crypto
                  </>
                )}
              </Title> */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {SUBLINE_MAPPER[lpd || 'default'](theme)}
                {lpd &&
                  lpd !== 'openai' &&
                  lpd !== 'spend' &&
                  lpd !== 'payments' &&
                  lpd !== 'yield' &&
                  showFreeGift && (
                    <SubTitle
                      style={{ color: theme.text.regular, fontWeight: 600 }}
                    >
                      Your first digital gift is on us.
                    </SubTitle>
                  )}
                {lpd === 'openai' && showPrestockBonus && (
                  <SubTitle
                    style={{
                      color: theme.text.regular,
                      fontWeight: 600,
                      marginTop: 10,
                    }}
                  >
                    +10% bonus added to your pre-stock gift.
                  </SubTitle>
                )}
              </div>
              <div
                style={
                  isMobile
                    ? {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                        alignItems: 'center',
                      }
                    : {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                      }
                }
              >
                <Button
                  style={
                    isMobile
                      ? {
                          marginTop: lpd === 'spend' ? '5px' : '20px',
                          width: lpd === 'spend' ? '175px' : '50%',
                        }
                      : {
                          marginTop: '0px',
                          width: lpd === 'spend' ? '175px' : undefined,
                        }
                  }
                  clickAnimation='bouncy'
                  onClick={handleCtaClick}
                  fullWidth={isMobile}
                >
                  {getCtaLabel()}
                </Button>
              </div>
            </Column>
            {!(lpd === 'spend' && isMobile) && (
              <Column
                style={
                  lpd === 'spend' && !isMobile
                    ? { width: '35%', maxWidth: '35%' }
                    : { width: 'fit-content' }
                }
              >
                {lpd === 'spend' && !isMobile ? (
                  <div
                    style={{
                      marginTop: 28,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 16,
                    }}
                  >
                    <GiftCardsSectionTitle>
                      Brands you already use
                    </GiftCardsSectionTitle>
                    {isLoadingCatalogue ? (
                      <HeroGiftCardsGrid>
                        {[...Array(4)].map((_, i) => (
                          <div
                            key={i}
                            style={{
                              aspectRatio: '16/9',
                              borderRadius: 8,
                              background:
                                theme.background.secondary ||
                                theme.background.primary,
                              border: `1px solid ${theme.border?.primary || 'transparent'}`,
                            }}
                          />
                        ))}
                      </HeroGiftCardsGrid>
                    ) : (
                      <HeroGiftCardsGrid>
                        {heroGiftCards.map((card) => (
                          <SpendGalleryGiftCard
                            key={card.id}
                            card={card}
                            showPriceRange={false}
                            showBrandName={false}
                            onClick={() => {
                              hapticsImpactLight();
                              sendEvent(
                                EVENT_SPEND_GALLERY_OPENED_FROM_LANDING_PAGE,
                              );
                              sendEvent(EVENT_SPEND_GALLERY_CARD_CLICKED, {
                                gift_card_id: card.id,
                                gift_card_brand: card.brand,
                                gift_card_currency: card.currency,
                              });
                              sendMetaEvent('ViewContent', {
                                content_ids: [String(card.id)],
                                content_type: 'product',
                                content_name: card.brand,
                                currency: card.currency,
                              });
                              navigate(`/app/spend/${card.id}`);
                            }}
                          />
                        ))}
                      </HeroGiftCardsGrid>
                    )}
                  </div>
                ) : (
                  <>
                    {lpd === 'yield' ? (
                      <div style={{ position: 'relative' }}>
                        <MobileImage
                          onClick={() => {
                            hapticsImpactLight();
                            handleCtaClick();
                          }}
                          src={'/landing-v2/yield.webp'}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            top: isMobile ? '35%' : '30%',
                            left: 0,
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <p
                            style={{
                              color: 'black',
                              fontSize: isMobile ? 14 : 18,
                              fontWeight: 400,
                            }}
                          >
                            You've been gifted{' '}
                            <span
                              style={{
                                fontWeight: 600,
                                color: textColor,
                                transition: 'color 0.1s ease-out',
                              }}
                            >
                              ${animatedValue.toFixed(2).replace('.', ',')}
                            </span>
                          </p>
                        </div>
                      </div>
                    ) : (
                      <MobileImage
                        onClick={() => {
                          hapticsImpactLight();
                          handleCtaClick();
                        }}
                        src={IMAGER_MAPPER[lpd || 'default']}
                      />
                    )}
                  </>
                )}
              </Column>
            )}
          </Container>
        </WidthContainer>
        {lpd !== 'spend' && !isMobile && (
          <BackedByAlliance>
            <a href='https://alliance.xyz/companies/deks' target='_blank'>
              <img
                width={200}
                src={`https://alliance.xyz/share/backed-by.png${selectedTheme === 'light' ? '?mode=light' : ''}`}
                alt='Backed by Alliance'
              />
            </a>
          </BackedByAlliance>
        )}
      </ContainerBackground>
      <div style={{ height: siblingHeight }} />
    </>
  );
};

export default Hero;
